import { authQueryKeys } from '@/config/queryKeys';
import { RECENT_LOGOUT_SUPPRESSION_MS, ROUTE_PATHS, isPostLoginCallback, isPublicRoute } from '@/constants';
import { canNavigateTo } from '@/lib/navigation-guards';
import { handleUnauthorized } from '@/lib/shared/auth-shared';
import type { User } from '@/types';
import type { AuthData, AuthUtils } from '@/types/auth';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { useAuthQuery } from '../api/index.ts';

function useAuthStatus(): AuthData {
  const onPublicRoute = isPublicRoute(window.location.pathname);

  const userQuery = useAuthQuery({ enabled: !onPublicRoute });

  const queryClient = useQueryClient();
  const previousAuthStatus = useRef<boolean | null>(null);

  // Detect when user is unauthenticated and redirect to login
  useEffect(() => {
    const isAuthenticated = userQuery.data !== null && userQuery.data !== undefined && !userQuery.isPending;

    // Skip redirect if we're still loading or already on a public route
    if (userQuery.isPending || isPublicRoute(window.location.pathname)) {
      return;
    }

    // After a server-side login redirect the session cookie may not be visible
    // to the very first /auth/me request (timing / cookie propagation race).
    // Suppress the redirect so the query can revalidate naturally instead of
    // bouncing the user back to the login page unnecessarily.
    const postLoginCallback = isPostLoginCallback(window.location.pathname, window.location.search);

    // Case 1: Session expiration - user was authenticated and now is not
    const sessionExpired = previousAuthStatus.current === true && !isAuthenticated;

    // Case 2: Initial unauthenticated state - user was never authenticated
    const initiallyUnauthenticated = previousAuthStatus.current === null && !isAuthenticated;

    // isError covers every non-ok /auth/me answer except 401, which resolves to `null` — so 403
    // and 5xx land here and being signed out does not. Skipping avoids clearClientSession(); it
    // does not make the session look live, since the status below still reads UNAUTHENTICATED.
    if (
      (sessionExpired || initiallyUnauthenticated) &&
      !userQuery.isPending &&
      !userQuery.isError &&
      !postLoginCallback
    ) {
      // Skip redirect if we're already on the login page
      // Use router.state.location directly to avoid hook context issues during initialization
      if (window.location.pathname !== ROUTE_PATHS.LOGIN) {
        // Avoid invoking unauthorized handling during a recent logout window
        // or when the URL contains login=success (post-login callback).
        try {
          const lastLogout = Number(sessionStorage.getItem('__last_logout_ts')) || 0;
          const now = Date.now();
          // Scoped to this document, not just to 15s: sessionStorage outlives the page that wrote
          // the stamp, so a logout kept suppressing the *next* page's only redirect (POR-433). The
          // race worth suppressing can only happen inside the document that cleared the session.
          const loggedOutThisDocument = lastLogout >= Math.floor(performance.timeOrigin);
          const isRecentLogout = lastLogout && now - lastLogout < RECENT_LOGOUT_SUPPRESSION_MS && loggedOutThisDocument;

          // Respect any nav lock that may have been set after login to allow
          // the session cookie to propagate. If a nav lock exists and is
          // still in the future, skip invoking the unauthorized handler.
          const rawLock = sessionStorage.getItem('__nav_lock_until');
          const lockUntil = rawLock ? Number(rawLock) : 0;
          const isNavLocked = lockUntil && !Number.isNaN(lockUntil) && now < lockUntil;

          // canNavigateTo is intentionally NOT called here: onUnauthorized's own 500ms debounce
          // (INVOCATION_WINDOW_MS) is the call-site protection, and lib/router.tsx gates the
          // navigation on a composite key (`/login|{…search}`) a bare '/login' token cannot match.
          if (!isRecentLogout && !isNavLocked) {
            handleUnauthorized();
          }
        } catch {
          // If storage access fails, fall back to previous behavior.
          if (canNavigateTo(ROUTE_PATHS.LOGIN)) {
            handleUnauthorized();
          }
        }
      }
    }

    // Kept in step with the outer gate above, which skips on isError and in the post-login window:
    // recording a verdict there would disqualify both triggers for this instance once a real answer
    // arrives. The inner guards (nav lock, recent logout) do still latch — see issue #373.
    if (!userQuery.isPending && !userQuery.isError && !postLoginCallback) {
      previousAuthStatus.current = isAuthenticated;
    }
    // Clear last logout marker when we become authenticated
    if (isAuthenticated) {
      try {
        sessionStorage.removeItem('__last_logout_ts');
      } catch {}
    }
  }, [userQuery.data, userQuery.isPending, userQuery.isError]);

  const utils: AuthUtils = {
    signOut: () => {
      queryClient.setQueryData(authQueryKeys.me(), null);
    },
    userQuery,
  };

  // Check if query is still loading
  if (userQuery.isPending) {
    return { ...utils, user: null, status: 'PENDING' };
  }

  // Reported as UNAUTHENTICATED without the teardown a real 401 gets, so a 403/5xx greys the app
  // out with no redirect and no other trace — this log is the only record that it happened.
  if (userQuery.isError) {
    console.error('[useAuthStatus] /auth/me failed; reporting UNAUTHENTICATED without teardown', {
      error: userQuery.error?.message,
      failureCount: userQuery.failureCount,
      pathname: window.location.pathname,
    });
    return { ...utils, user: null, status: 'UNAUTHENTICATED' };
  }

  // Check if we have valid user data
  if (userQuery.data !== null && userQuery.data !== undefined) {
    return { ...utils, user: userQuery.data, status: 'AUTHENTICATED' };
  }

  // No data or null data means unauthenticated
  return { ...utils, user: null, status: 'UNAUTHENTICATED' };
}

export { useAuthStatus };
export type { AuthData, User };
